import AppPageTitle from '@nxsys/components/AppPageTitle';
import AppsContainer from '@nxsys/components/AppsContainer';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import ReportItem from './components/ReportItem';
import ReportSection from './components/ReportSection';
import ExportPensionModal from './pension/components/ExportPensionModal';
import { hasAllDepartmentCompany } from '@nxsys/utils/department-scope';

const Reports = () => {
  const { messages, formatMessage } = useTypedIntl();
  const navigate = useNavigateWithQuery();
  const [isOpenExportPensionModal, setIsOpenExportPensionModal] =
    useState(false);
  const { isBureau, payrollCountry, user } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isIreland = payrollCountry === IRELAND;
  // P30/P32 and pension reports/export are company-wide output — never
  // filtered to a subset of departments (same rule menuFilter.ts applies to
  // the sidebar entries for these features). Bank files are not: the backend
  // lists only the files that pay the caller's own departments.
  const canSeeCompanyLevelReports = hasAllDepartmentCompany(user);

  // Payroll Reports
  const payrollReports = useMemo(
    () => [
      ...(!isBureau
        ? [
            {
              title: formatMessage(
                { id: 'common.draftPayrollWithModel' },
                // NX-5228: Ireland calls the model RCT. The PAYE tile below
                // already distinguishes them for bureau; this branch never did.
                { model: isIreland ? 'RCT' : 'CIS' },
              ),
              onClick: () => navigate('/reports/payroll/draft-payroll-summary'),
            },
          ]
        : []),
      {
        title: formatMessage(
          { id: 'common.draftPayrollWithModel' },
          { model: isBureau ? (isIreland ? 'PAYE/RCT' : 'PAYE') : 'PAYE/Umbrella' },
        ),
        onClick: () =>
          navigate('/reports/payroll/draft-payroll-payee-umbrella-summary'),
      },
      // NX-5774: the CIS Payment Summary route is UK-only (see
      // reportRoutesForCountry), so on Ireland this tile only led to a 404.
      ...(!isBureau && !isIreland
        ? [
            {
              title: messages['common.cisPaymentSummary'],
              onClick: () => navigate('/reports/payroll/cis-payment-summary'),
            },
          ]
        : []),
      {
        title: isBureau
          ? (isIreland ? 'Payment Summary PAYE/RCT' : messages['common.paymentSummaryPaye'])
          : messages['common.paymentSummaryReportPayeeUmbrella'],
        onClick: () => navigate('/reports/payroll/payment-summary'),
      },
      {
        title: messages['common.holidayPayBalanceDetail'],
        onClick: () => navigate('/reports/payroll/holiday-pay-balance-detail'),
      },
      {
        title: messages['common.holidayPayBalanceSummary'],
        onClick: () => navigate('/reports/payroll/holiday-pay-balance-summary'),
      },
      {
        title: messages['common.netPayReport'],
        onClick: () => navigate('/reports/payroll/net-pay-report'),
      },
      ...(!isIreland
        ? [
            {
              title: messages['common.52WeekSummary'],
              onClick: () =>
                navigate('/reports/payroll/52-week-average-summary-report'),
            },
          ]
        : []),
    ],
    [messages, formatMessage, navigate, isBureau, isIreland],
  );

  // HMRC Reports (P30/P32 — company-wide, hidden for a restricted user)
  const hmrcReports = useMemo(
    () =>
      !isIreland && canSeeCompanyLevelReports
        ? [
            {
              title: 'P30',
              onClick: () => navigate('/reports/payroll/p30'),
            },
            {
              title: 'P32',
              onClick: () => navigate('/reports/payroll/p32'),
            },
          ]
        : [],
    [navigate, isIreland, canSeeCompanyLevelReports],
  );

  // Pension Reports (company-wide, hidden for a restricted user)
  const pensionReports = useMemo(
    () =>
      canSeeCompanyLevelReports
        ? [
            ...(!isIreland
              ? [
                  {
                    title: messages['common.pensionContributionReport'],
                    onClick: () =>
                      navigate('/reports/pension/contribution-report', {
                        query: { from: '/reports' },
                      }),
                  },
                  {
                    title: messages['common.autoEnrolmentStatusReport'],
                    onClick: () =>
                      navigate('/reports/pension/auto-enrolment-status-report'),
                  },
                ]
              : [
                  {
                    title:
                      messages['common.contributionsReport'] ||
                      'Contributions Report',
                    onClick: () =>
                      navigate('/reports/pension/contributions-ireland', {
                        query: { from: '/reports' },
                      }),
                  },
                ]),
            {
              title: messages['common.exportPension'],
              onClick: () => setIsOpenExportPensionModal(true),
            },
          ]
        : [],
    [messages, navigate, isIreland, canSeeCompanyLevelReports],
  );

  // Management Reports (UK only; Employment Intermediary & CIS are Umbrella-only)
  const managementReports = useMemo(
    () =>
      !isIreland
        ? [
            ...(!isBureau
              ? [
                  {
                    title: messages['common.employmentIntermediaryReport'],
                    onClick: () =>
                      navigate('/reports/management/employment-intermediary'),
                  },
                  {
                    title: messages['common.cisStateOfEarnings'],
                    onClick: () =>
                      navigate('/reports/management/earning-summary'),
                  },
                ]
              : []),
            {
              title: messages['common.dormantWorker'],
              onClick: () => navigate('/reports/management/dormant-worker'),
            },
          ]
        : [],
    [messages, navigate, isIreland, isBureau],
  );

  // Employee Reports (AWR is Umbrella-only + UK-only)
  const employeeReports = useMemo(
    () => [
      {
        title: messages['common.rightToWorkExpiry'],
        onClick: () => navigate('/reports/employee/right-to-work'),
      },
      ...(!isIreland && !isBureau
        ? [
            {
              title: messages['common.awr'],
              onClick: () => navigate('/reports/employee/awr'),
            },
          ]
        : []),
      {
        title: messages['common.wtr'],
        onClick: () => navigate('/reports/employee/wtr'),
      },
    ],
    [messages, navigate, isIreland, isBureau],
  );

  // Bank Files Reports (kept to the caller's departments by the backend)
  const bankFilesReports = useMemo(
    () => [
      {
        title: messages['sidebar.app.reports.bankFiles'] || 'Bank Files',
        onClick: () => navigate('/reports/bank-files'),
      },
    ],
    [messages, navigate],
  );

  // Attachment Orders Reports (UK only)
  const attachmentOrdersReports = useMemo(
    () =>
      !isIreland
        ? [
            {
              title:
                messages['sidebar.app.reports.attachmentOrders'] ||
                messages['common.attachmentOrders'] ||
                'Attachment Orders',
              onClick: () => navigate('/reports/attachment-orders-report'),
            },
          ]
        : [],
    [messages, navigate, isIreland],
  );

  // On Hold Worker Reports
  const onHoldWorkerReports = useMemo(
    () => [
      {
        title:
          messages['sidebar.app.reports.onHoldWorker'] ||
          messages['common.workerOnHold'] ||
          'On Hold Worker',
        onClick: () => navigate('/reports/on-hold-worker'),
      },
    ],
    [messages, navigate],
  );

  const renderTitle = useCallback(() => {
    return (
      <AppPageTitle
        prefix={messages['common.reports'] || 'Reports'}
        separator=""
        title=""
      />
    );
  }, [messages]);

  return (
    <AppsContainer fullView type="bottom" title={renderTitle()} loading={false}>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        {/* Left Column - Payroll Section (Full Height) */}
        <div className="flex flex-col gap-4">
          <ReportSection
            title={messages['sidebar.app.reports.payroll'] || 'Payroll'}
          >
            {payrollReports.map((report) => (
              <ReportItem
                key={report.title}
                title={report.title}
                onClick={report.onClick}
              />
            ))}
          </ReportSection>

          {pensionReports.length > 0 && (
            <ReportSection
              title={messages['sidebar.app.reports.pension'] || 'Pension'}
            >
              {pensionReports.map((report) => (
                <ReportItem
                  key={report.title}
                  title={report.title}
                  onClick={report.onClick}
                />
              ))}
            </ReportSection>
          )}

          {bankFilesReports.length > 0 && (
            <ReportSection
              title={
                messages['sidebar.app.reports.bankFiles'] || 'Bank Files'
              }
            >
              {bankFilesReports.map((report) => (
                <ReportItem
                  key={report.title}
                  title={report.title}
                  onClick={report.onClick}
                />
              ))}
            </ReportSection>
          )}

          <ReportSection
            title={
              messages['sidebar.app.reports.onHoldWorker'] || 'On Hold Worker'
            }
          >
            {onHoldWorkerReports.map((report) => (
              <ReportItem
                key={report.title}
                title={report.title}
                onClick={report.onClick}
              />
            ))}
          </ReportSection>
        </div>

        {/* Right Column - Pension, Management, Employee, Attachment Orders, On Hold Worker, HMRC */}
        <div className="flex flex-col gap-4">
          {managementReports.length > 0 && (
            <ReportSection
              title={messages['sidebar.app.reports.management'] || 'Management'}
            >
              {managementReports.map((report) => (
                <ReportItem
                  key={report.title}
                  title={report.title}
                  onClick={report.onClick}
                />
              ))}
            </ReportSection>
          )}

          <ReportSection
            title={messages['sidebar.app.reports.employee'] || 'Employee'}
          >
            {employeeReports.map((report) => (
              <ReportItem
                key={report.title}
                title={report.title}
                onClick={report.onClick}
              />
            ))}
          </ReportSection>

          {attachmentOrdersReports.length > 0 && (
            <ReportSection
              title={
                messages['sidebar.app.reports.attachmentOrders'] ||
                'Attachment Orders'
              }
            >
              {attachmentOrdersReports.map((report) => (
                <ReportItem
                  key={report.title}
                  title={report.title}
                  onClick={report.onClick}
                />
              ))}
            </ReportSection>
          )}

          {hmrcReports.length > 0 && (
            <ReportSection
              title={messages['sidebar.app.reports.hmrc'] || 'HMRC'}
            >
              {hmrcReports.map((report) => (
                <ReportItem
                  key={report.title}
                  title={report.title}
                  onClick={report.onClick}
                />
              ))}
            </ReportSection>
          )}
        </div>
      </div>
      <ExportPensionModal
        isOpen={isOpenExportPensionModal}
        toggle={() => setIsOpenExportPensionModal(false)}
      />
    </AppsContainer>
  );
};

export default Reports;
